/**
 * Currency Store
 * 
 * จัดการสกุลเงินและการแปลงค่า
 * - 1 เครดิต = X สกุลเงินท้องถิ่น (ขึ้นอยู่กับ multiplier)
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ============================================
// Currency Configuration
// ============================================

export interface CurrencyConfig {
  code: string           // ISO code: THB, LAK, KHR, MMK
  lang: string           // Language code: th, lo, km
  name: string           // ชื่อเต็ม
  nameTh: string         // ชื่อภาษาไทย
  nameLocal: string      // ชื่อในภาษานั้น
  symbol: string         // สัญลักษณ์
  flagCode: string       // สำหรับ FlagIcon: th, la, kh, mm, vn
  multiplier: number     // 1 เครดิต = X สกุลเงิน
  decimalPlaces: number  // ทศนิยม
  locale: string         // สำหรับ format
  enabled: boolean       // แสดงใน UI หรือไม่
}

// ============================================
// Currency Data
// ============================================

export const CURRENCIES: CurrencyConfig[] = [
  {
    code: 'THB',
    lang: 'th',
    name: 'Thai Baht',
    nameTh: 'ไทย',
    nameLocal: 'ภาษาไทย',
    symbol: '฿',
    flagCode: 'th',
    multiplier: 1,
    decimalPlaces: 2,
    locale: 'th-TH',
    enabled: true,
  },
  {
    code: 'LAK',
    lang: 'lo',
    name: 'Lao Kip',
    nameTh: 'ลาว',
    nameLocal: 'ພາສາລາວ',
    symbol: '₭',
    flagCode: 'la',
    multiplier: 1000,
    decimalPlaces: 0,
    locale: 'lo-LA',
    enabled: true,
  },
  {
    code: 'KHR',
    lang: 'km',
    name: 'Cambodian Riel',
    nameTh: 'กัมพูชา',
    nameLocal: 'ភាសាខ្មែរ',
    symbol: '៛',
    flagCode: 'kh',
    multiplier: 100,
    decimalPlaces: 0,
    locale: 'km-KH',
    enabled: true,
  },
  // Hidden currencies - ซ่อนใน UI แต่ยังใช้ได้ในระบบ
  {
    code: 'MMK',
    lang: 'my',
    name: 'Myanmar Kyat',
    nameTh: 'เมียนมา',
    nameLocal: 'မြန်မာ',
    symbol: 'K',
    flagCode: 'mm',
    multiplier: 50,
    decimalPlaces: 0,
    locale: 'my-MM',
    enabled: false, // ซ่อน
  },
  {
    code: 'VND',
    lang: 'vi',
    name: 'Vietnamese Dong',
    nameTh: 'เวียดนาม',
    nameLocal: 'Tiếng Việt',
    symbol: '₫',
    flagCode: 'vn',
    multiplier: 600,
    decimalPlaces: 0,
    locale: 'vi-VN',
    enabled: false, // ซ่อน
  },
]

// Get only enabled currencies
export const ENABLED_CURRENCIES = CURRENCIES.filter(c => c.enabled)

// ============================================
// Store Interface
// ============================================

interface CurrencyState {
  // Current currency
  currencyCode: string
  
  // Display settings
  showBothValues: boolean     // แสดงทั้งเครดิตและสกุลเงินท้องถิ่น
  primaryDisplay: 'credit' | 'local'  // แสดงอะไรหลัก
  
  // Actions
  setCurrency: (code: string) => void
  setShowBothValues: (show: boolean) => void
  setPrimaryDisplay: (display: 'credit' | 'local') => void
  
  // Getters
  getCurrentCurrency: () => CurrencyConfig
  
  // Conversion functions
  creditToLocal: (credit: number) => number
  localToCredit: (local: number) => number
  
  // Format functions
  formatCredit: (credit: number) => string
  formatLocal: (credit: number) => string
  formatBoth: (credit: number) => string
}

// ============================================
// Store Implementation
// ============================================

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => ({
      // Initial state
      currencyCode: 'THB',
      showBothValues: false,
      primaryDisplay: 'credit',
      
      // Actions
      setCurrency: (code) => {
        const currency = CURRENCIES.find(c => c.code === code)
        if (currency) {
          set({ currencyCode: code })
        }
      },
      
      setShowBothValues: (show) => set({ showBothValues: show }),
      setPrimaryDisplay: (display) => set({ primaryDisplay: display }),
      
      // Getters
      getCurrentCurrency: () => {
        const { currencyCode } = get()
        return CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0]
      },
      
      // Conversion
      creditToLocal: (credit) => {
        const currency = get().getCurrentCurrency()
        return credit * currency.multiplier
      },
      
      localToCredit: (local) => {
        const currency = get().getCurrentCurrency()
        return local / currency.multiplier
      },
      
      // Format Credit (always same format)
      formatCredit: (credit) => {
        return new Intl.NumberFormat('th-TH', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(credit)
      },
      
      // Format Local Currency
      formatLocal: (credit) => {
        const currency = get().getCurrentCurrency()
        const localAmount = credit * currency.multiplier
        
        return new Intl.NumberFormat(currency.locale, {
          minimumFractionDigits: currency.decimalPlaces,
          maximumFractionDigits: currency.decimalPlaces,
        }).format(localAmount) + ' ' + currency.symbol
      },
      
      // Format Both (for display)
      formatBoth: (credit) => {
        const { formatCredit, formatLocal, primaryDisplay, showBothValues, getCurrentCurrency } = get()
        const currency = getCurrentCurrency()
        
        if (!showBothValues || currency.multiplier === 1) {
          return primaryDisplay === 'credit' 
            ? formatCredit(credit) 
            : formatLocal(credit)
        }
        
        if (primaryDisplay === 'credit') {
          return `${formatCredit(credit)} (${formatLocal(credit)})`
        } else {
          return `${formatLocal(credit)} (${formatCredit(credit)} เครดิต)`
        }
      },
    }),
    {
      name: 'currency-storage',
      partialize: (state) => ({
        currencyCode: state.currencyCode,
        showBothValues: state.showBothValues,
        primaryDisplay: state.primaryDisplay,
      }),
    }
  )
)

// ============================================
// Hook for easy use
// ============================================

export function useCurrency() {
  const store = useCurrencyStore()
  
  return {
    currency: store.getCurrentCurrency(),
    currencyCode: store.currencyCode,
    showBothValues: store.showBothValues,
    primaryDisplay: store.primaryDisplay,
    
    // Actions
    setCurrency: store.setCurrency,
    setShowBothValues: store.setShowBothValues,
    setPrimaryDisplay: store.setPrimaryDisplay,
    
    // Format
    format: store.formatBoth,
    formatCredit: store.formatCredit,
    formatLocal: store.formatLocal,
    
    // Convert
    toLocal: store.creditToLocal,
    toCredit: store.localToCredit,
    
    // All currencies for selection (only enabled)
    currencies: ENABLED_CURRENCIES,
    
    // All currencies (including hidden)
    allCurrencies: CURRENCIES,
  }
}
